import { useQuery } from '@tanstack/react-query';
import { api, type TodayScheduleResponse, type Schedule } from '../lib/api';

/**
 * useTodaySchedule - Fetch today's schedule for a specific employee.
 *
 * @param email - The employee's email address
 * @returns Query result with today's schedule data, loading, and error states
 */
export function useTodaySchedule(email: string | undefined) {
  return useQuery({
    queryKey: ['schedule', 'today', email],
    queryFn: async () => {
      if (!email) return null;
      const response = await api.schedules.getEmployeeToday(email);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch schedule');
      }
      return response.data as TodayScheduleResponse;
    },
    enabled: !!email,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}

/**
 * useScheduleForDate - Fetch schedule for a specific employee on a specific date.
 *
 * Used for look-ahead feature to preview upcoming schedules.
 *
 * @param email - The employee's email address
 * @param date - The date to fetch schedule for (YYYY-MM-DD format)
 * @returns Query result with schedule data, loading, and error states
 */
export function useScheduleForDate(email: string | undefined, date: string | undefined) {
  return useQuery({
    queryKey: ['schedule', 'date', email, date],
    queryFn: async () => {
      if (!email || !date) return null;
      const response = await api.schedules.getEmployeeToday(email, date);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch schedule');
      }
      return response.data as TodayScheduleResponse;
    },
    enabled: !!email && !!date,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchInterval: 5 * 60 * 1000, // Refetch every 5 minutes
  });
}

/**
 * useAllTodaySchedules - Fetch all employee schedules for today.
 *
 * Used for admin/manager views to see all scheduled routes.
 *
 * @returns Query result with array of all today's schedules, loading, and error states
 */
export function useAllTodaySchedules() {
  return useQuery({
    queryKey: ['schedules', 'today', 'all'],
    queryFn: async () => {
      const response = await api.schedules.getToday();
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch schedules');
      }
      return response.data as Schedule[];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000, // 10 minutes cache
    refetchInterval: 5 * 60 * 1000,
  });
}

/**
 * useWeekSchedules - Fetch schedules for an entire week.
 *
 * Returns schedules grouped by date for week view display.
 *
 * @param startDate - Optional start date (YYYY-MM-DD format), defaults to current week
 * @returns Query result with schedules grouped by date, loading, and error states
 */
export function useWeekSchedules(startDate?: string) {
  return useQuery({
    queryKey: ['schedules', 'week', startDate],
    queryFn: async () => {
      const response = await api.schedules.getWeek(startDate);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to fetch week schedules');
      }
      return response.data as Record<string, Schedule[]>;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000, // 10 minutes cache
  });
}
